/**
 * PendingWorkflowsCard
 * Dashboard card summarizing active workflows (sagas + standalone atomics) in the slot
 * Quick Actions used to occupy. Sourced from the SAME `useWorkflowPanel()` context the
 * WorkflowRail/WorkflowSheet use — a shared react-query cache and `dismiss` mutation, no
 * independent fetch — so dismissing a row here is immediately reflected in the rail.
 */

import { Skeleton } from '../ui/skeleton';
import { PageSection } from '../layout/index';
import { useWorkflowPanel } from '../workflow-panel/useWorkflowPanel';
import { DismissWorkflowButton } from '../workflow-panel/DismissWorkflowButton';
import { workflowDisplayLabel, type WorkflowListItem } from '../../types/workflow';

interface DashboardWorkflowRowProps {
  workflow: WorkflowListItem;
  onDismiss: (id: string) => Promise<void>;
}

/** One compact row: progress ring, title, status text, always-visible Dismiss button.
 *  Informational + dismissible only — no drill-in/click-to-navigate. */
function DashboardWorkflowRow({ workflow, onDismiss }: DashboardWorkflowRowProps) {
  const label = workflowDisplayLabel(workflow);

  let progressPercent: number;
  let ringLabel: string;
  let statusText: string;

  if (workflow.kind === 'saga') {
    // SagaRow's existing math (SagaRow.tsx lines 33-41, 98-103), reused as arithmetic only.
    const completedCount = workflow.members.filter((m) => m.status === 'completed').length;
    const totalCount = workflow.members.length;
    progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
    ringLabel = `${completedCount}/${totalCount}`;
    const activeMember = workflow.members.find((m) => m.status === 'active');
    const unsatisfiedCount = activeMember
      ? activeMember.preconditions.filter((p) => !p.satisfied).length
      : 0;
    statusText =
      completedCount === totalCount
        ? 'Complete'
        : `${unsatisfiedCount > 0 ? `${unsatisfiedCount} item${unsatisfiedCount !== 1 ? 's' : ''} needed · ` : ''}${progressPercent}% done`;
  } else {
    // AtomicRow's existing math (AtomicRow.tsx lines 30-38, 65-69), reused as arithmetic only.
    const member = workflow.members[0];
    const unsatisfiedCount = member ? member.preconditions.filter((p) => !p.satisfied).length : 0;
    const totalSteps = member?.totalSteps ?? 0;
    const currentStep = member?.currentStep ?? null;
    progressPercent =
      totalSteps > 0 && currentStep != null ? Math.round(((currentStep - 1) / totalSteps) * 100) : 0;
    ringLabel = `${progressPercent}%`;
    statusText =
      unsatisfiedCount > 0
        ? `${unsatisfiedCount} item${unsatisfiedCount !== 1 ? 's' : ''} needed`
        : `${progressPercent}% done`;
  }

  return (
    <div
      data-testid={`dashboard-workflow-row-${workflow.id}`}
      className="relative flex items-center gap-3 p-3 rounded-lg border border-border bg-background"
      role="listitem"
    >
      {/* Progress ring — SagaRow.tsx's ring markup verbatim, sized down to fit a compact row */}
      <div className="relative shrink-0 w-8 h-8" aria-hidden="true">
        <svg viewBox="0 0 36 36" className="h-8 w-8 -rotate-90">
          <circle
            cx="18" cy="18" r="15"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            className="text-muted/40"
          />
          <circle
            cx="18" cy="18" r="15"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeDasharray={`${progressPercent * 0.942} 94.2`}
            className="text-primary transition-all duration-500"
          />
        </svg>
        {/* text-[9px]: intentionally sub-xs to fit inside the 32px progress ring; no Tailwind token equivalent */}
        <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-foreground">
          {ringLabel}
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{label}</p>
        <p className="text-xs text-muted-foreground">{statusText}</p>
      </div>

      <DismissWorkflowButton
        workflowId={workflow.id}
        accessibleLabel={`Dismiss ${label} workflow`}
        testId={`dashboard-workflow-dismiss-btn-${workflow.id}`}
        onDismiss={onDismiss}
      />
    </div>
  );
}

export function PendingWorkflowsCard() {
  const { workflows, isLoading, isError, isFetching, refetch, dismiss } = useWorkflowPanel();
  const activeWorkflows = workflows.filter((w) => w.status !== 'dismissed');
  // Only surface the error state when there is NOTHING else to show — mirrors WorkflowList's
  // own showError so a background-refetch failure doesn't replace a good, stale-but-valid list.
  const showError = isError && activeWorkflows.length === 0;

  return (
    <PageSection
      title="Pending Workflows"
      badge={activeWorkflows.length > 0 ? activeWorkflows.length : undefined}
      headingLevel={2}
      data-testid="dashboard-pending-workflows"
    >
      <div className="flex flex-col gap-2" role="list" aria-label="Pending workflows">
        {isLoading && (
          <>
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </>
        )}

        {/* Error state — same copy/structure as WorkflowList's, this card's own root testid */}
        {!isLoading && showError && (
          <div
            role="alert"
            data-testid="dashboard-pending-workflows-error"
            className="flex flex-col items-center justify-center py-8 text-center"
          >
            <div
              className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center mb-3"
              aria-hidden="true"
            >
              <svg className="w-5 h-5 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-foreground">Couldn’t load workflows</p>
            <p className="text-xs text-muted-foreground mt-0.5 mb-3">Something went wrong fetching your workflows.</p>
            <button
              type="button"
              data-testid="dashboard-pending-workflows-retry"
              onClick={() => refetch()}
              disabled={isFetching}
              aria-busy={isFetching}
              className="px-4 min-h-[44px] text-sm font-medium text-primary border border-primary/40 rounded-md hover:bg-primary/5 disabled:opacity-60 disabled:cursor-default"
            >
              {isFetching ? 'Retrying…' : 'Try again'}
            </button>
          </div>
        )}

        {!isLoading && !showError && activeWorkflows.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div
              className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mb-3"
              aria-hidden="true"
            >
              <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-foreground">All caught up</p>
            <p className="text-xs text-muted-foreground mt-0.5">No active workflows</p>
          </div>
        )}

        {!isLoading &&
          activeWorkflows.length > 0 &&
          activeWorkflows.map((workflow) => (
            <DashboardWorkflowRow key={workflow.id} workflow={workflow} onDismiss={dismiss} />
          ))}
      </div>
    </PageSection>
  );
}

export default PendingWorkflowsCard;
